import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import * as Sentry from '@sentry/browser'
import qz from 'qz-tray'
import { showFailure, showSuccess } from '../Toast'

interface ConveyorPortContextProps {
  port: string | null
  isOpen: boolean
  loading: boolean
  sendMessage: (message: string) => Promise<boolean>
  selectAndOpenPort: (port: string) => Promise<void>
  closePort: () => Promise<void>
}

const ConveyorPortContext = createContext<ConveyorPortContextProps | undefined>(undefined)

interface ConveyorProviderProps {
  children: ReactNode
}

const connectionProperties = {
  baudRate: 19200,
  dataBits: 8,
  stopBits: 1,
  parity: 'none'
}

function stringToAsciiArray(input: string) {
  return input.split('').map(char => char.charCodeAt(0))
}

export const ConveyorPortProvider: React.FC<ConveyorProviderProps> = ({ children }) => {
  const [loading, setLoading] = useState(false)
  const [port, setPort] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState(false)

  const displayError = (error: any) => {
    showFailure(error.message)
    Sentry.captureException(error)
  }

  useEffect(() => {
    const waitForQzTray = async (attempts = 5, delay = 2000) => {
      for (let i = 0; i < attempts; i += 1) {
        if (qz.websocket.isActive()) {
          return true
        }
        await new Promise(resolve => setTimeout(resolve, delay))
      }
      return false
    }

    const reconnectPort = async () => {
      const wasManuallyClosed = localStorage.getItem('serialPortManuallyClosed') === 'true'
      if (wasManuallyClosed) return

      const savedPort = localStorage.getItem('serialPortOpen')
      if (savedPort) {
        const isQzReady = await waitForQzTray()
        if (!isQzReady) {
          // eslint-disable-next-line no-console
          console.error('QZ Tray is not ready. Skipping reconnection.')
          return
        }

        try {
          await qz.serial.openPort(savedPort, connectionProperties)
          setPort(savedPort)
          setIsOpen(true)
          showSuccess('Reconnected to serial port.')
        } catch (error) {
          displayError(error)
        }
      }
    }

    if (localStorage.getItem('serialPortOpen')) {
      reconnectPort()
    }
  }, [])

  const closePort = async () => {
    if (!port) return
    try {
      setLoading(true)
      await qz.serial.closePort(port)
      showSuccess(`Port: [${port}] was closed successfully.`)
      localStorage.setItem('serialPortManuallyClosed', 'true')
      localStorage.removeItem('serialPortOpen')
      setPort(null)
      setIsOpen(false)
    } catch (error) {
      setLoading(false)
      displayError(error)
    } finally {
      setLoading(false)
    }
  }

  const selectAndOpenPort = async selectedPort => {
    if (!selectedPort) return
    try {
      await qz.serial.openPort(selectedPort, connectionProperties)
      showSuccess(`${selectedPort} port is opened`)
      localStorage.setItem('serialPortOpen', selectedPort)
      localStorage.removeItem('serialPortManuallyClosed')
      setIsOpen(true)
      setPort(selectedPort)
    } catch (error) {
      displayError(error)
    } finally {
      setLoading(false)
    }
  }

  const sendMessage = async (message: string) => {
    if (!port || !isOpen) {
      showFailure('Please select and open a port first.')
      return false
    }
    try {
      setLoading(true)
      const asciiMessage = stringToAsciiArray(message)
      await qz.serial.sendData(port, asciiMessage)
      return true
    } catch (error) {
      showFailure(`Error sending message: ${error.message}`)
      Sentry.captureException(error)
      setLoading(false)
      return false
    } finally {
      setLoading(false)
    }
  }

  return (
    <ConveyorPortContext.Provider value={{ port, isOpen, loading, sendMessage, selectAndOpenPort, closePort }}>
      {children}
    </ConveyorPortContext.Provider>
  )
}

export const useWebPortConveyor = () => useContext(ConveyorPortContext)
